import { useEffect } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import type { StoreLocatorLayoutSlot as StoreLocatorLayoutSlotSchema } from '@amplience/content-types/typings/s-store-locator-layout';
import {
  ArrowLeftSmall,
  GoogleMapPin,
  StoreDetailType,
  StoreMap,
  TypographyStyles,
  createGoogleMapIcon,
  getItem,
  getLocalizedValue,
  getStoreLocatorBaseUrl,
  media,
  removeItem,
  renderAmplienceSlotComponents,
  setItem,
  useLocaleContext,
  useMediaMatch,
  useStar2,
  useStaticContext,
} from '@storefront/lib-global';
import type { StoreMapMarker, StoreType } from '@storefront/lib-global';
import type { FormattedScheduleType } from '@storefront/lib-global/globalComponents/storeItems/utils';
import { featureNames, useFormattedSchedule } from '@storefront/lib-global/globalComponents/storeItems/utils';
import { StoreStatusComponent } from '@storefront/lib-global/globalComponents/storeStatus';
import { sendStoreLocatorEvent } from '@storefront/lib-global/utils/gtm/events/store_locator/sendStoreLocatorEvent';
import { Locale } from '@storefront/lib-global/utils/internationalization/localeHelper';
import { ContentBreadcrumb } from '../contentBreadcrumb';
import type { BreadcrumbDataType } from '../contentBreadcrumb';
import {
  Back,
  BookFittingCta,
  DepartmentLink,
  LocatorContent,
  SlotContent,
  StoreCardMap,
  StoreCardMapContent,
  StoreDetail,
  StoreDetailButton,
  StoreDetailContent,
  StoreDetailSection,
  StoreDetailWrapper,
  StoreLocatorContentBodyText,
  StoreLocatorDetailHours,
  StoreName,
} from './StoreLocatorDetail.styles';
import { storeLocatorPageAmplienceComponents } from './storeLocatorPageAmplienceComponents';

type StoreLocatorDetailsProps = {
  storeData: StoreType[];
  storeLocatorLayout: StoreLocatorLayoutSlotSchema;
  breadcrumbData: BreadcrumbDataType[];
};

const AmplienceComponents = {
  ...storeLocatorPageAmplienceComponents,
};

export function StoreLocatorDetail({ storeData, storeLocatorLayout, breadcrumbData }: StoreLocatorDetailsProps) {
  const { storeLocatorContentBody, slotContent, kidsFittingLink } = storeLocatorLayout || {};
  const { objectID, _geoloc, displayName, address, openingHours, timezone, features, departments, storeType } =
    storeData[0] || {};
  const { mallName, streetNumber, country, town, streetName, phone, province, region, postalCode } = address || {};
  const { locale } = useLocaleContext();
  const { t } = useTranslation(['lib-global-common']);
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const icon = createGoogleMapIcon(<GoogleMapPin />);
  const formattedSchedule: FormattedScheduleType[] = useFormattedSchedule(openingHours, true);
  const { previousURL } = useStaticContext();
  const { isStar2 } = useStar2();

  const activeFeatures = features?.filter((item) => item.isActive === true && item.name in featureNames);
  const departmentsInLocale = departments?.filter((department) => department[`lable.${locale}`]);
  const formatRegionOrProvince = (locales: string[]) => (locales.includes(locale) ? region : province) || '';
  const provinceOrRegion = formatRegionOrProvince([Locale.EN_US, Locale.EN_CA, Locale.FR_CA]);
  const formatStreetName = streetName?.endsWith(',') ? streetName.slice(0, -1) : streetName;
  const formatStreetNumber = streetNumber?.endsWith(',') ? streetNumber.slice(0, -1) : streetNumber;

  const store =
    _geoloc?.lat && _geoloc?.lng
      ? ([
          {
            lat: _geoloc.lat,
            lng: _geoloc.lng,
            mapLabel: '',
            id: objectID || '',
            icon,
          },
        ] as StoreMapMarker[])
      : undefined;

  const handleGetDirections = (lat?: number, lng?: number) => () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${lat || 0},${lng || 0}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handlePrintClick = () => {
    window.print();
  };

  useEffect(() => {
    sendStoreLocatorEvent({
      step: 'details',
      postcode: getItem('storeLocatorQuery', true, 'sessionStorage')?.locationQuery || '',
    });
  }, []);

  const pdpStockAvailabilityData = getItem('pdpStockAvailabilityData', true, 'sessionStorage');
  const previousResultsUrl = pdpStockAvailabilityData?.path || getStoreLocatorBaseUrl(locale);

  useEffect(() => {
    if (!previousURL.endsWith(`/${previousResultsUrl}`)) removeItem('storeLocatorQuery', 'sessionStorage');
    if (!previousURL.endsWith('-p')) removeItem('pdpStockAvailabilityData', 'sessionStorage');
    if (getItem('isPdpBackToResults', false, 'sessionStorage')) removeItem('isPdpBackToResults');
  }, [previousURL, previousResultsUrl]);

  const handleBackToResultsClick = () => {
    if (pdpStockAvailabilityData) {
      setItem('isPdpBackToResults', true, false, 'sessionStorage');
    }
  };

  return (
    <StoreDetailSection>
      {breadcrumbData?.length ? <ContentBreadcrumb breadcrumbData={breadcrumbData} /> : null}
      <LocatorContent id="print-section">
        <StoreDetailWrapper data-testid="storeDetailsWrapper" $isStar2={!!isStar2}>
          <Back data-testid="backToResultsButton" onClick={handleBackToResultsClick}>
            <ArrowLeftSmall />
            <Link href={`/${previousResultsUrl}`} passHref>
              <a>{t('store.locator.detail.back')}</a>
            </Link>
          </Back>
          <StoreName data-testid="storeName">{displayName}</StoreName>
          <StoreDetail>
            <span>{t('store.locator.detail.address')}</span>
            <StoreDetailContent data-testid="storeAddress">
              <p>{`${mallName ? `${mallName}, ` : ''}${
                formatStreetNumber ? `${formatStreetNumber}, ` : ''
              }${formatStreetName}, ${town}`}</p>
              <p>{`${provinceOrRegion ? `${provinceOrRegion}, ` : ''}${postalCode}, ${country}`}</p>
            </StoreDetailContent>
          </StoreDetail>
          {phone && (
            <StoreDetail>
              <span>{t('store.locator.detail.telephone')}</span>
              <StoreDetailContent data-testid="storePhoneNumber">
                <p>{phone}</p>
              </StoreDetailContent>
            </StoreDetail>
          )}
          {(!isStar2 || (storeType !== StoreDetailType.STOCKIST && !!openingHours?.length)) && (
            <StoreDetail>
              <StoreLocatorDetailHours>
                {t('store.locator.detail.hours')}
                <StoreStatusComponent openingHours={openingHours} timezone={timezone} />:
              </StoreLocatorDetailHours>

              <StoreDetailContent data-testid="storeOpenHours">
                {formattedSchedule.map(({ startDay, endDay, openingTime, closingTime }) => (
                  <p key={startDay}>{`${startDay}${endDay ? ` ${t('pdp.store.days.divider')} ${endDay}` : ''}: ${
                    openingTime ? `${openingTime} - ${closingTime}` : t('pdp.store.closed')
                  }`}</p>
                ))}
              </StoreDetailContent>
            </StoreDetail>
          )}
          {departmentsInLocale?.length ? (
            <StoreDetail>
              <span>{t('store.locator.detail.departments')}</span>
              <StoreDetailContent>
                {departmentsInLocale.map((department, idx) => (
                  <span key={`department-link-${idx}`}>
                    {departmentsInLocale.length !== 1 && idx === departmentsInLocale.length - 1 ? ' & ' : ' '}
                    <Link href={department.url} passHref>
                      <DepartmentLink>
                        {idx === departmentsInLocale.length - 1 || idx === departmentsInLocale.length - 2
                          ? `${department[`lable.${locale}`]}`
                          : `${department[`lable.${locale}`]},`}
                      </DepartmentLink>
                    </Link>
                  </span>
                ))}
              </StoreDetailContent>
            </StoreDetail>
          ) : null}
          {activeFeatures?.length ? (
            <StoreDetail>
              <span>{t('store.locator.detail.services')}</span>
              <ul data-testid="storeServicesList">
                {activeFeatures.map(({ name }, index) => (
                  <li key={`store-service-${index}`}>{t(`store.feature.${name}`)}</li>
                ))}
              </ul>
            </StoreDetail>
          ) : null}
          <StoreDetailButton
            type="button"
            onClick={handleGetDirections(_geoloc?.lat, _geoloc?.lng)}
            data-testid="getStoreDirectionsLink"
          >
            {t('store.locator.detail.direction')}
          </StoreDetailButton>
          <StoreDetailButton type="button" onClick={handlePrintClick} data-testid="printStoreDetailsLink">
            {t('store.locator.detail.print')}
          </StoreDetailButton>
          {features?.findIndex((item) => item.name === featureNames.kidsFitting && item.isActive) !== -1 && !isStar2 && (
            <Link href={getLocalizedValue(kidsFittingLink) || ''} passHref>
              <BookFittingCta
                disabled={!kidsFittingLink}
                title={kidsFittingLink ? undefined : t('store.locator.booking.restricted')}
                data-testid="bookKidsFitting"
              >
                {t('store.locator.detail.book')}
              </BookFittingCta>
            </Link>
          )}
        </StoreDetailWrapper>
        <StoreCardMapContent>
          <StoreCardMap>
            <StoreMap height={isDesktop ? '667px' : '282px'} stores={store} mapId="store-map" />
          </StoreCardMap>
          {storeLocatorContentBody && (
            <StoreLocatorContentBodyText
              text={getLocalizedValue(storeLocatorContentBody.richText)}
              fontSize={TypographyStyles.Body.Small.Regular}
            />
          )}
        </StoreCardMapContent>
      </LocatorContent>
      <SlotContent>
        {Array.isArray(slotContent) && renderAmplienceSlotComponents(slotContent, AmplienceComponents)}
      </SlotContent>
    </StoreDetailSection>
  );
}

export default StoreLocatorDetail;
